import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../../types/common/types';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { OperationModeData } from '../../../types/operation-mode-data';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzTooltipWebComponent } from '../../../../ui/gzTooltip';
import { MonthlyProductType } from '../../../../license/types/MonthlyProductType';
import { Feature } from '@nirvana/shared/constants';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { ModulesData } from '../../../types/package-data';
import { GzLinkWebComponent } from '../../../../ui/gzLinkComponent';
import { translateFn } from '../../../../utils/translator.helper';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { PackageModulesFormViewData } from '../types/PackageModulesFormViewData';
import { PackageActionContext } from '../../types/PackageActionContext';
import { ModuleFieldComponentsMap } from '../types/ModuleFieldComponentsMap';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { PackageModulesInfoLinks } from '../types/PackageModulesInfoLinks';
import { GzToggleWebComponent } from '../../../../ui/gzToggle';
import { OnReconfigureAgentModuleIsChecked } from '../../../../network/reconfigureAgent/reconfigureAgentPanel/types';

export class PackageModulesFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  public modulesFields: ModuleFieldComponentsMap;
  public initialProductType: MonthlyProductType | undefined;

  public dom: {
    $wrapper: HTMLElement;

    $securityModulesAndRolesSubtitle: GzTitleWebComponent;

    $operationModeFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $edrBoxInfo: GzInfoBoxWebComponent;

    $showAllModulesToggle: GzToggleWebComponent;
    $showAllModulesBoxInfo: GzInfoBoxWebComponent;

    $moduleWindowsTooltip: GzTooltipWebComponent[];
    $moduleWindowsWorkstationTooltip: GzTooltipWebComponent[];
    $moduleWindowsServersTooltip: GzTooltipWebComponent[];
    $moduleMacOsTooltip: GzTooltipWebComponent[];
    $moduleLinuxTooltip: GzTooltipWebComponent[];

    $moduleEncryptionLearnMore: GzLinkWebComponent;
    $moduleUpdateServerLearnMore: GzLinkWebComponent;
    $modulePatchManagementServerLearnMore: GzLinkWebComponent;
    $moduleMailServersLearnMore: GzLinkWebComponent;

    $updateServerCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $patchManagementServerCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $mailServersCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

    $modulesAndRolesValidationBoxInfo: GzInfoBoxWebComponent;

    $removeCompetitorsCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $removeCompetitorsBoxInfo: GzInfoBoxWebComponent;
    $additionalSettingsSubtitle: GzTitleWebComponent;
    $rolesSubtitle: GzTitleWebComponent;
    $modulesSubtitle: GzTitleWebComponent;
    $osCompatibilitySubtitle: GzTitleWebComponent;
    $rolesSection: HTMLElement;
    $additionalSettingsSection: HTMLElement;
    $headerSettingsSection: HTMLElement;
  };

  getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('#package-modules-form-wrapper'),

      $securityModulesAndRolesSubtitle: this.shadow.querySelector('#modules-roles-subtitle'),

      $operationModeFormField: this.shadow.querySelector('#operation-mode-form-field'),
      $edrBoxInfo: this.shadow.querySelector('#edr-box-info'),

      $showAllModulesToggle: this.shadow.querySelector('#show-all-modules-toggle'),
      $showAllModulesBoxInfo: this.shadow.querySelector('#show-all-modules-info-box'),

      $moduleWindowsTooltip: this.shadow.querySelectorAll('.module-windows-tooltip'),
      $moduleWindowsWorkstationTooltip: this.shadow.querySelectorAll('.module-windows-workstation-tooltip'),
      $moduleWindowsServersTooltip: this.shadow.querySelectorAll('.module-windows-servers-tooltip'),
      $moduleMacOsTooltip: this.shadow.querySelectorAll('.module-macos-tooltip'),
      $moduleLinuxTooltip: this.shadow.querySelectorAll('.module-linux-tooltip'),

      $moduleEncryptionLearnMore: this.shadow.querySelector('#module-encryption-info'),
      $moduleUpdateServerLearnMore: this.shadow.querySelector('#module-update-server-info'),
      $modulePatchManagementServerLearnMore: this.shadow.querySelector('#module-patch-management-server-info'),
      $moduleMailServersLearnMore: this.shadow.querySelector('#module-mail-servers-info'),

      $updateServerCheckbox: this.shadow.querySelector('#module-update-server'),
      $patchManagementServerCheckbox: this.shadow.querySelector('#module-patch-management-server'),
      $mailServersCheckbox: this.shadow.querySelector('#module-mail-servers'),

      $modulesAndRolesValidationBoxInfo: this.shadow.querySelector('#modules-and-roles-validation-info-box'),

      $additionalSettingsSubtitle: this.shadow.querySelector('#additional-settings-subtitle'),
      $removeCompetitorsCheckbox: this.shadow.querySelector('#remove-competitors'),
      $removeCompetitorsBoxInfo: this.shadow.querySelector('#remove-competitors-box-info'),
      $rolesSubtitle: this.shadow.querySelector('#roles-subtitle'),
      $modulesSubtitle: this.shadow.querySelector('#modules-subtitle'),
      $osCompatibilitySubtitle: this.shadow.querySelector('#os-compatibility-subtitle'),
      $rolesSection: this.shadow.querySelector('#roles-section'),
      $additionalSettingsSection: this.shadow.querySelector('#additional-settings-section'),
      $headerSettingsSection: this.shadow.querySelector('#header-settings-section'),
    };

    this.initModulesCheckboxes();
    this.getTranslations();
    this.setTranslatedStrings();
  }

  private initModulesCheckboxes(): void {
    this.modulesFields = {
      antimalware: {
        key: 'antimalware',
        $uiCheckbox: this.shadow.querySelector('#module-antimalware'),
        checked: true,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [],
      },
      atc: {
        key: 'avc',
        $uiCheckbox: this.shadow.querySelector('#module-atc'),
        checked: true,
        availableForProducts: [MonthlyProductType.EndpointSecurity, MonthlyProductType.BitdefenderEdr],
        availableForFeatures: [],
      },

      exploitMitigation: {
        key: 'exploitMitigation',
        $uiCheckbox: this.shadow.querySelector('#module-aae'),
        checked: true,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [Feature.PRODUCT_FEATURE_EXPLOIT_MITIGATION_USABLE, Feature.PRODUCT_FEATURE_FRAT_VISIBLE],
      },

      containerProtection: {
        key: 'containerProtection',
        $uiCheckbox: this.shadow.querySelector('#module-container-protection'),
        checked: false,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [
          Feature.PRODUCT_FEATURE_CONTAINER_PROTECTION_USABLE,
          Feature.PRODUCT_FEATURE_FRAT_VISIBLE,
        ],
      },

      firewall: {
        key: 'firewall',
        $uiCheckbox: this.shadow.querySelector('#module-firewall'),
        checked: true,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [Feature.PRODUCT_FEATURE_FIREWALL_USABLE, Feature.PRODUCT_FEATURE_FRAT_VISIBLE],
      },

      networkProtection: {
        key: 'networkProtection',
        $uiCheckbox: this.shadow.querySelector('#module-network-protection'),
        checked: true,
        availableForProducts: [MonthlyProductType.EndpointSecurity, MonthlyProductType.BitdefenderEdr],
        availableForFeatures: [],
      },

      userControl: {
        key: 'userControl',
        $uiCheckbox: this.shadow.querySelector('#module-user-control'),
        checked: true,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [Feature.PRODUCT_FEATURE_CONTENT_CONTROL_USABLE],
      },

      antiphishing: {
        key: 'antiphishing',
        $uiCheckbox: this.shadow.querySelector('#module-antiphishing'),
        checked: true,
        availableForProducts: [MonthlyProductType.EndpointSecurity, MonthlyProductType.BitdefenderEdr],
        availableForFeatures: [],
      },

      trafficScan: {
        key: 'trafficScan',
        $uiCheckbox: this.shadow.querySelector('#module-traffic-scan'),
        checked: true,
        availableForProducts: [MonthlyProductType.EndpointSecurity, MonthlyProductType.BitdefenderEdr],
        availableForFeatures: [],
      },

      networkMonitor: {
        key: 'networkMonitor',
        $uiCheckbox: this.shadow.querySelector('#module-network-attack-defense'),
        checked: true,
        availableForProducts: [MonthlyProductType.EndpointSecurity, MonthlyProductType.BitdefenderEdr],
        availableForFeatures: [Feature.PRODUCT_FEATURE_NETWORK_MONITOR_USABLE],
      },

      deviceControl: {
        key: 'deviceControl',
        $uiCheckbox: this.shadow.querySelector('#module-device-control'),
        checked: false,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [Feature.PRODUCT_FEATURE_DEVICE_CONTROL_USABLE, Feature.PRODUCT_FEATURE_FRAT_VISIBLE],
      },

      powerUser: {
        key: 'powerUser',
        $uiCheckbox: this.shadow.querySelector('#module-power-user'),
        checked: false,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [],
      },

      applicationControl: {
        key: 'applicationControl',
        $uiCheckbox: this.shadow.querySelector('#module-application-control'),
        checked: false,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [Feature.PRODUCT_FEATURE_APPLICATION_CONTROL, Feature.PRODUCT_FEATURE_FRAT_VISIBLE],
      },

      encryption: {
        key: 'encryption',
        $uiCheckbox: this.shadow.querySelector('#module-encryption'),
        checked: false,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [Feature.PRODUCT_FEATURE_VOLUME_ENCRYPTION_USABLE, Feature.PRODUCT_FEATURE_FRAT_VISIBLE],
      },

      patchManagement: {
        key: 'patchManagement',
        $uiCheckbox: this.shadow.querySelector('#module-patch-management'),
        checked: false,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [Feature.PRODUCT_FEATURE_PATCH_MANAGEMENT_USABLE, Feature.PRODUCT_FEATURE_FRAT_VISIBLE],
      },

      integrityMonitor: {
        key: 'integrityMonitor',
        $uiCheckbox: this.shadow.querySelector('#module-integrity-monitor'),
        checked: false,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [Feature.PRODUCT_FEATURE_INTEGRITY_MONITOR_USABLE, Feature.PRODUCT_FEATURE_FRAT_VISIBLE],
      },

      edrSensor: {
        key: 'edrSensor',
        $uiCheckbox: this.shadow.querySelector('#module-edr-sensor'),
        checked: true,
        availableForProducts: [MonthlyProductType.EndpointSecurity, MonthlyProductType.BitdefenderEdr],
        availableForFeatures: [Feature.PRODUCT_FEATURE_EDR_USABLE, Feature.PRODUCT_FEATURE_FRAT_VISIBLE],
      },

      frat: {
        key: 'frat',
        $uiCheckbox: this.shadow.querySelector('#module-frat'),
        checked: false,
        availableForProducts: [MonthlyProductType.EndpointSecurity],
        availableForFeatures: [Feature.PRODUCT_FEATURE_FRAT_VISIBLE],
      },
    };
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$securityModulesAndRolesSubtitle.innerText = translateFn('Security Modules and Roles');
    this.dom.$operationModeFormField.label = translateFn('Operation Mode');

    this.dom.$showAllModulesToggle.innerHTML = translateFn('Show all modules');
    this.dom.$showAllModulesBoxInfo.innerText = translateFn(
      'You can uninstall modules that are no longer included in your license, or belong to a license expired and removed.'
    );

    this.dom.$operationModeFormField.Input.noResultsFound = this.commonStrings.noResultsFound;
    this.dom.$edrBoxInfo.innerHTML = translateFn(
      'Bitdefender leverages the Advanced Threat Control and Network Protection technologies to enhance the detection capability of EDR. The {edr} mode is compatible with any security solution existing on the endpoint. {newLine} For endpoint protection, switch to the {detectionAndPrevention} mode. If you cannot access this option, contact your Bitdefender sales representative for more details.',
      {
        newLine: '<br>',
        edr: '<b>EDR (Report only)</b>',
        detectionAndPrevention: '<b>Detection and prevention</b>',
      }
    );

    this.dom.$moduleWindowsTooltip.forEach(el => {
      el.tooltipText = translateFn('Windows servers and workstations');
    });
    this.dom.$moduleWindowsWorkstationTooltip.forEach(el => {
      el.tooltipText = translateFn('Windows workstations');
    });
    this.dom.$moduleWindowsServersTooltip.forEach(el => {
      el.tooltipText = translateFn('Windows servers');
    });
    this.dom.$moduleMacOsTooltip.forEach(el => {
      el.tooltipText = translateFn('macOS');
    });
    this.dom.$moduleLinuxTooltip.forEach(el => {
      el.tooltipText = translateFn('Linux');
    });

    this.dom.$moduleEncryptionLearnMore.innerText = translateFn('Learn more');
    this.dom.$moduleUpdateServerLearnMore.innerText = translateFn('Learn more');
    this.dom.$modulePatchManagementServerLearnMore.innerText = translateFn('Learn more');
    this.dom.$moduleMailServersLearnMore.innerText = translateFn('Learn more');

    this.modulesFields.antimalware.$uiCheckbox.Input.label = translateFn('Antimalware');
    this.modulesFields.atc.$uiCheckbox.Input.label = translateFn('Advanced Threat Control');
    this.modulesFields.exploitMitigation.$uiCheckbox.Input.label = translateFn('Advanced Anti-Exploit');
    this.modulesFields.containerProtection.$uiCheckbox.Input.label = translateFn('Container Protection');
    this.modulesFields.firewall.$uiCheckbox.Input.label = translateFn('Firewall');
    this.modulesFields.networkProtection.$uiCheckbox.Input.label = translateFn('Network Protection');
    this.modulesFields.userControl.$uiCheckbox.Input.label = translateFn('Content Control');
    this.modulesFields.antiphishing.$uiCheckbox.Input.label = this.dependencies.translateFn('Antiphishing');
    this.modulesFields.trafficScan.$uiCheckbox.Input.label = this.dependencies.translateFn('Web Traffic Scan');
    this.modulesFields.networkMonitor.$uiCheckbox.Input.label = translateFn('Network Attack Defense');
    this.modulesFields.deviceControl.$uiCheckbox.Input.label = translateFn('Device Control');
    this.modulesFields.powerUser.$uiCheckbox.Input.label = translateFn('Power user');
    this.modulesFields.applicationControl.$uiCheckbox.Input.label = translateFn('Application Control');
    this.modulesFields.encryption.$uiCheckbox.Input.label = translateFn('Encryption');
    this.modulesFields.patchManagement.$uiCheckbox.Input.label = translateFn('Patch Management');
    this.modulesFields.patchManagement.$uiCheckbox.Input.label = translateFn('Patch Management');
    this.modulesFields.integrityMonitor.$uiCheckbox.Input.label = translateFn('Integrity Monitoring');
    this.modulesFields.edrSensor.$uiCheckbox.Input.label = translateFn('EDR Sensor');
    this.modulesFields.frat.$uiCheckbox.Input.label = translateFn('Free Risk Assessment Tool');

    this.dom.$updateServerCheckbox.Input.label = translateFn('Relay');
    this.dom.$patchManagementServerCheckbox.Input.label = translateFn('Patch Management Cache Server');
    this.dom.$mailServersCheckbox.Input.label = translateFn('Exchange Protection');

    this.dom.$modulesSubtitle.innerText = translateFn('Modules');
    this.dom.$osCompatibilitySubtitle.innerText = translateFn('OS Compatibility');
    this.dom.$rolesSubtitle.innerText = translateFn('Roles');
    this.dom.$modulesAndRolesValidationBoxInfo.innerText = translateFn('At least one module or role must be selected.');
    this.dom.$additionalSettingsSubtitle.innerText = translateFn('Additional Settings');
    this.dom.$removeCompetitorsCheckbox.Input.label = translateFn('Remove Competitors');
    this.dom.$removeCompetitorsBoxInfo.innerText = translateFn(
      'Skipping competitor removal before installation is not recommended. Running multiple security solutions on the same machine may lead to performance or incompatibility issues.'
    );
  }

  buildSuccessView(viewData: PackageModulesFormViewData) {
    this.createFormGroup();

    this.setOperationModeOptions(viewData.operationModes);
    this.setLearnMoreLinks(this.dom.$moduleEncryptionLearnMore, PackageModulesInfoLinks.Module.FullDiskEncryption);
    this.setLearnMoreLinks(this.dom.$moduleUpdateServerLearnMore, PackageModulesInfoLinks.Roles.Relay);
    this.setLearnMoreLinks(
      this.dom.$modulePatchManagementServerLearnMore,
      PackageModulesInfoLinks.Roles.PatchManagementCacheServer
    );
    this.setLearnMoreLinks(this.dom.$moduleMailServersLearnMore, PackageModulesInfoLinks.Roles.ExchangeProtection);

    if (viewData.context === PackageActionContext.Create) {
      this.buildInitialConfig();
    }

    this.dom.$modulesAndRolesValidationBoxInfo.innerText = viewData.displayOnlyModules ? translateFn('At least one module must be selected.') : this.dom.$modulesAndRolesValidationBoxInfo.innerText;
    this.setSectionsVisibility(viewData);
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private setSectionsVisibility(viewData: PackageModulesFormViewData): void {
    toggleVisibility(this.dom.$headerSettingsSection, !viewData.displayOnlyModules);
    toggleVisibility(this.dom.$rolesSection, !viewData.displayOnlyModules);
    toggleVisibility(this.dom.$additionalSettingsSection, !viewData.displayOnlyModules);
  }

  private createFormGroup(): void {
    this.dom.$operationModeFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.formGroup = FormGroup.build({
      settings: {
        modules: {
          antimalware: this.modulesFields.antimalware.$uiCheckbox,
          avc: this.modulesFields.atc.$uiCheckbox,
          exploitMitigation: this.modulesFields.exploitMitigation.$uiCheckbox,
          containerProtection: this.modulesFields.containerProtection.$uiCheckbox,
          firewall: this.modulesFields.firewall.$uiCheckbox,
          userControl: this.modulesFields.userControl.$uiCheckbox,
          antiphishing: this.modulesFields.antiphishing.$uiCheckbox,
          trafficScan: this.modulesFields.trafficScan.$uiCheckbox,
          networkMonitor: this.modulesFields.networkMonitor.$uiCheckbox,
          deviceControl: this.modulesFields.deviceControl.$uiCheckbox,
          powerUser: this.modulesFields.powerUser.$uiCheckbox,
          applicationControl: this.modulesFields.applicationControl.$uiCheckbox,
          encryption: this.modulesFields.encryption.$uiCheckbox,
          patchManagement: this.modulesFields.patchManagement.$uiCheckbox,
          integrityMonitor: this.modulesFields.integrityMonitor.$uiCheckbox,
          edrSensor: this.modulesFields.edrSensor.$uiCheckbox,
          frat: this.modulesFields.frat.$uiCheckbox,
          updateServer: this.dom.$updateServerCheckbox,
          patchManagementServer: this.dom.$patchManagementServerCheckbox,
          mailServers: this.dom.$mailServersCheckbox,
        },
        productType: this.dom.$operationModeFormField,
        removeCompetitors: this.dom.$removeCompetitorsCheckbox,
      },
    });

    this.formGroup.setDisabled(true);
  }

  private buildInitialConfig(): void {
    const defaultValueProductType = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_USABLE])
      ? MonthlyProductType.BitdefenderEdr
      : MonthlyProductType.EndpointSecurity;

    this.setNetworkProtectionCheckbox(true);

    this.dom.$removeCompetitorsCheckbox.Input.checked = true;
    toggleVisibility(this.dom.$removeCompetitorsBoxInfo, false);
    toggleVisibility(this.dom.$edrBoxInfo, defaultValueProductType === MonthlyProductType.BitdefenderEdr);
    toggleVisibility(this.dom.$patchManagementServerCheckbox, false);

    this.dom.$operationModeFormField.Input.setFormValue(defaultValueProductType);
  }

  private setNetworkProtectionCheckbox(isDisabled: boolean): void {
    this.modulesFields.networkProtection.$uiCheckbox.disabled = isDisabled;
  }

  private setOperationModeOptions(operationModes: OperationModeData[]): void {
    this.dom.$operationModeFormField.Input.options = operationModes;
  }

  public setPackageData(modulesData: ModulesData): void {
    this.initialProductType = modulesData.productType;
    this.formGroup.value = {
      settings: {
        productType: modulesData.productType,
        modules: modulesData.modules,
        removeCompetitors: modulesData.removeCompetitors,
      },
    };
    this.formGroup.validate(true);
  }

  private setLearnMoreLinks(moduleLearnMore: GzLinkWebComponent, links): void {
    if (Application.Main.product.isCloud()) {
      if (Application.Main.company.isPartner()) {
        moduleLearnMore.href = links.Partners;
        return;
      }
      moduleLearnMore.href = links.Cloud;
    } else {
      moduleLearnMore.href = links.OnPremise;
    }
  }

  public checkIfAtLeastOnModuleIsChecked(): { valid: boolean } {
    let isValid = false;

    for (const moduleField of Object.values(this.modulesFields)) {
      if (moduleField.$uiCheckbox.Input.checked) {
        isValid = true;
        break;
      }
    }

    const mailServersCheckbox = this.dom.$mailServersCheckbox.Input;
    const updateServerCheckbox = this.dom.$updateServerCheckbox.Input;
    const patchManagementServerCheckbox = this.dom.$patchManagementServerCheckbox.Input;

    if (mailServersCheckbox.checked || updateServerCheckbox.checked || patchManagementServerCheckbox.checked) {
      isValid = true;
    }

    toggleVisibility(this.dom.$modulesAndRolesValidationBoxInfo, !isValid);

    this.dom.$wrapper.dispatchEvent(new OnReconfigureAgentModuleIsChecked(!isValid));

    // return always true because validation is handled by $modulesAndRolesValidationBoxInfo, we don't need to make checkboxes red
    return {
      valid: true,
    };
  }
}
